import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { ESTADOS } from '~/constant';
import * as api from '~/Services/Service';
import { cnpjValido, somenteAlfanumerico } from '~/validationSchemas';
import { validarCPF } from '~/Utils/Helpers';

const limparCpfCnpj = value => {
  if (!value) return '';
  const alfanumerico = somenteAlfanumerico(value);
  if (/[A-Z]/.test(alfanumerico) || alfanumerico.length > 11) return alfanumerico;
  return String(value).replace(/\D/g, '');
};

const validarCpfCnpjFornecedor = (value, tipoPessoa) => {
  const documento = limparCpfCnpj(value);
  if (!documento) return true;
  if (tipoPessoa === '1') return cnpjValido(documento);
  if (tipoPessoa === '0') return validarCPF(documento);
  return documento.length === 14 ? cnpjValido(documento) : validarCPF(documento);
};

const validationSchema = Yup.object().shape({
  apelido: Yup.string().required('Apelido é obrigatório').max(30, 'Máximo 30 caracteres'),
  razao_social: Yup.string()
    .required('Razão Social é obrigatória')
    .max(40, 'Máximo 40 caracteres'),
  tipo_pessoa: Yup.string().required('Tipo Pessoa é obrigatório'),
  codigo_cidade: Yup.number()
    .nullable()
    .required('Cidade é obrigatória')
    .positive('Cidade é obrigatória'),
  fone_1: Yup.string()
    .required('Fone 1 é obrigatório')
    .max(15, 'Máximo 15 caracteres')
    .test('max-digits', 'Telefone deve ter no máximo 13 dígitos', function (val) {
      if (!val) return true;
      return val.replace(/\D/g, '').length <= 13;
    }),
  fone_2: Yup.string()
    .nullable()
    .max(15, 'Máximo 15 caracteres')
    .test('max-digits', 'Telefone deve ter no máximo 13 dígitos', function (val) {
      if (!val) return true;
      return val.replace(/\D/g, '').length <= 13;
    }),
  fax: Yup.string()
    .nullable()
    .max(15, 'Máximo 15 caracteres')
    .test('max-digits', 'Celular deve ter no máximo 13 dígitos', function (val) {
      if (!val) return true;
      return val.replace(/\D/g, '').length <= 13;
    }),
  email: Yup.string().email('Email inválido').nullable(),
  cpf: Yup.string().when(['internacional', 'tipo_pessoa'], {
    is: internacional => !internacional,
    then: Yup.string()
      .required('CNPJ/CPF é obrigatório')
      .test('cpf-cnpj-valido', 'CNPJ/CPF inválido', function (value) {
        return validarCpfCnpjFornecedor(value, this.parent.tipo_pessoa);
      }),
    otherwise: Yup.string().nullable(),
  }),
});

const INITIAL_VALUES = {
  apelido: '',
  codigo_contabil: '',
  razao_social: '',
  tipo_pessoa: '1',
  cpf: '',
  rg: '',
  internacional: false,
  conta_pj: false,
  endereco: '',
  bairro: '',
  cep: '',
  estado: '',
  codigo_cidade: null,
  fone_1: '',
  fone_2: '',
  fax: '',
  contato: '',
  email: '',
};

const ModalFornecedor = ({ show, onHide, onSalvar, fornecedor, modoEdicao }) => {
  const [cidades, setCidades] = useState([]);
  const [cidadesById, setCidadesById] = useState({});
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);
  const [docDuplicata, setDocDuplicata] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (show && fornecedor && fornecedor.estado) {
      carregarCidades(fornecedor.estado);
    }
    if (!show) {
      setCidades([]);
      setCidadesById({});
      setDocDuplicata(null);
    }
  }, [show]);

  const carregarCidades = async uf => {
    if (!uf) {
      setCidades([]);
      setCidadesById({});
      return;
    }
    setLoadingCidades(true);
    try {
      const resp = await api.getCidades(uf);
      const lista = resp || [];
      setCidades(lista);
      const byId = {};
      lista.forEach(c => {
        byId[c.codigo] = c;
      });
      setCidadesById(byId);
    } catch (e) {
      setCidades([]);
      setCidadesById({});
    } finally {
      setLoadingCidades(false);
    }
  };

  const removerAcentos = str => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const filtroCidades = inputValue => {
    const termo = removerAcentos(inputValue.toLowerCase());
    return cidades
      .filter(c => removerAcentos(c.descricao.toLowerCase()).indexOf(termo) >= 0)
      .map(c => ({ value: c.codigo, label: c.descricao }));
  };

  const carregarCidadesAsync = (inputValue, callback) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      callback(filtroCidades(inputValue));
    }, 300);
  };

  const getCidadeValue = codigoCidade => {
    if (codigoCidade == null) {
      return {
        value: '',
        label: cidades.length === 0 ? 'Selecione o estado' : 'Selecione uma cidade',
      };
    }
    const found = cidadesById[codigoCidade];
    if (found) return { value: found.codigo, label: found.descricao };
    if (fornecedor && fornecedor.codigo_cidade === codigoCidade && fornecedor.cidade) {
      return { value: codigoCidade, label: fornecedor.cidade };
    }
    return { value: '', label: 'Selecione uma cidade' };
  };

  const getInitialValues = () => {
    if (!fornecedor) return INITIAL_VALUES;
    return {
      apelido: fornecedor.apelido || '',
      codigo_contabil: fornecedor.codigo_contabil || '',
      razao_social: fornecedor.razao_social || '',
      tipo_pessoa: fornecedor.tipo_pessoa || '1',
      cpf: fornecedor.cpf || '',
      rg: fornecedor.rg != null ? String(fornecedor.rg) : '',
      internacional: fornecedor.internacional === 'S',
      conta_pj: fornecedor.conta_pj === 'S',
      endereco: fornecedor.endereco || '',
      bairro: fornecedor.bairro || '',
      cep: fornecedor.cep ? String(fornecedor.cep) : '',
      estado: fornecedor.estado || '',
      codigo_cidade: fornecedor.codigo_cidade || null,
      fone_1: fornecedor.fone_1 != null ? String(fornecedor.fone_1) : '',
      fone_2: fornecedor.fone_2 != null ? String(fornecedor.fone_2) : '',
      fax: fornecedor.fax != null ? String(fornecedor.fax) : '',
      contato: fornecedor.contato || '',
      email: fornecedor.email || '',
    };
  };

  const handleBuscarCEP = async (cep, setFieldValue) => {
    const cepLimpo = (cep || '').replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCEP(true);
    try {
      const res = await api.buscarEnderecoPorCEP(cepLimpo);
      if (res) {
        if (res.logradouro) setFieldValue('endereco', res.logradouro);
        if (res.bairro) setFieldValue('bairro', res.bairro);
        if (res.uf) {
          setFieldValue('estado', res.uf);
          await carregarCidades(res.uf);
        }
        if (res.codigo_cidade) setFieldValue('codigo_cidade', res.codigo_cidade);
      }
    } catch (e) {
      toast.warn('CEP não encontrado');
    } finally {
      setBuscandoCEP(false);
    }
  };

  const handleBuscarCNPJ = async (cnpj, setFieldValue) => {
    const cnpjLimpo = limparCpfCnpj(cnpj);
    if (cnpjLimpo.length !== 14) return;
    setBuscandoCNPJ(true);
    try {
      const res = await api.consultarCNPJ(cnpjLimpo);
      if (res) {
        if (res.razao_social || res.nome) setFieldValue('razao_social', res.razao_social || res.nome);
        if (res.logradouro) setFieldValue('endereco', res.logradouro + (res.numero ? `, ${res.numero}` : ''));
        if (res.bairro) setFieldValue('bairro', res.bairro);
        if (res.cep) setFieldValue('cep', res.cep.replace(/\D/g, ''));
        if (res.uf) {
          setFieldValue('estado', res.uf);
          await carregarCidades(res.uf);
        }
        if (res.codigo_cidade) setFieldValue('codigo_cidade', res.codigo_cidade);
        setFieldValue('tipo_pessoa', '1');
      }
    } catch (e) {
      toast.warn('Não foi possível buscar dados do CNPJ');
    } finally {
      setBuscandoCNPJ(false);
    }
    await validarDocumentoDuplicata(cnpjLimpo, fornecedor ? fornecedor.codigo : null);
  };

  const validarDocumentoDuplicata = async (documento, excluirCodigo) => {
    if (!documento) {
      setDocDuplicata(null);
      return;
    }
    try {
      const res = await api.validarDocumentoCadUnico(documento, excluirCodigo);
      setDocDuplicata(res && !res.valido ? res : null);
    } catch (e) {
      setDocDuplicata(null);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const data = {
        apelido: values.apelido,
        codigo_contabil: values.codigo_contabil || null,
        razao_social: values.razao_social,
        tipo_pessoa: values.tipo_pessoa,
        cpf: values.internacional ? null : (values.cpf ? limparCpfCnpj(values.cpf) : null),
        rg: values.rg ? Number(values.rg) : null,
        internacional: values.internacional,
        conta_pj: values.conta_pj,
        endereco: values.endereco || null,
        bairro: values.bairro || null,
        cep: values.cep ? Number(values.cep.replace(/\D/g, '')) : null,
        codigo_cidade: values.codigo_cidade,
        fone_1: values.fone_1 ? Number(values.fone_1.replace(/\D/g, '')) : null,
        fone_2: values.fone_2 ? Number(values.fone_2.replace(/\D/g, '')) : null,
        fax: values.fax ? Number(values.fax.replace(/\D/g, '')) : null,
        contato: values.contato || null,
        email: values.email || null,
      };

      if (modoEdicao && fornecedor) {
        await api.atualizarCadUnicoFornecedor(fornecedor.codigo, data);
        toast.success('Fornecedor atualizado com sucesso!');
      } else {
        await api.criarCadUnicoFornecedor(data);
        toast.success('Fornecedor criado com sucesso!');
      }
      onSalvar();
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        'Erro ao salvar fornecedor';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="cuf-modal-wide" backdrop="static">
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          {modoEdicao ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit: formikSubmit, setFieldValue, setFieldTouched }) => (
          <Form onSubmit={formikSubmit}>
            <Modal.Body>
              {modoEdicao && fornecedor && fornecedor.total_contas > 0 && (
                <Alert variant="warning">
                  Este cadastro possui <strong>{fornecedor.total_contas}</strong> conta(s)
                  vinculada(s). Alterações serão propagadas para todas.
                </Alert>
              )}

              {docDuplicata && (
                <Alert variant="danger">
                  Já existe cadastro único para{' '}
                  <strong>{docDuplicata.cadastro_existente}</strong> com este documento.
                </Alert>
              )}

              {/* Linha 1: Apelido + Cód. Contábil + Tipo Pessoa + Checkboxes empilhadas */}
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      Apelido <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="apelido"
                      maxLength={30}
                      value={values.apelido}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.apelido && !!errors.apelido}
                    />
                    <Form.Control.Feedback type="invalid">{errors.apelido}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Cód. Contábil</Form.Label>
                    <Form.Control
                      name="codigo_contabil"
                      maxLength={10}
                      value={values.codigo_contabil}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      Tipo Pessoa <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      name="tipo_pessoa"
                      value={values.tipo_pessoa}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.tipo_pessoa && !!errors.tipo_pessoa}
                    >
                      <option value="">Selecione...</option>
                      <option value="0">Física</option>
                      <option value="1">Jurídica</option>
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{errors.tipo_pessoa}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} style={{ paddingTop: '0.5rem' }}>
                  <Form.Check
                    type="checkbox"
                    id="chk-internacional"
                    label="Internacional"
                    checked={values.internacional}
                    onChange={e => {
                      setFieldValue('internacional', e.target.checked);
                      if (e.target.checked) setFieldValue('cpf', '');
                      setDocDuplicata(null);
                    }}
                    style={{ marginBottom: '0.25rem' }}
                  />
                  <Form.Check
                    type="checkbox"
                    id="chk-conta-pj"
                    label="Conta Salário PJ"
                    checked={values.conta_pj}
                    onChange={e => setFieldValue('conta_pj', e.target.checked)}
                  />
                </Col>
              </Row>

              {/* Linha 2: Documento + Razão Social */}
              <Row>
                {!values.internacional && (
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>
                        CNPJ/CPF <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          name="cpf"
                          maxLength={18}
                          value={values.cpf}
                          onChange={e => {
                            handleChange(e);
                            const documento = limparCpfCnpj(e.target.value);
                            if (documento.length === 11) setFieldValue('tipo_pessoa', '0');
                            else if (documento.length === 14) setFieldValue('tipo_pessoa', '1');
                          }}
                          onBlur={async e => {
                            handleBlur(e);
                            const documento = limparCpfCnpj(e.target.value);
                            if (documento.length === 14) {
                              await handleBuscarCNPJ(e.target.value, setFieldValue);
                            } else if (documento.length === 11) {
                              await validarDocumentoDuplicata(
                                documento,
                                fornecedor ? fornecedor.codigo : null
                              );
                            }
                          }}
                          isInvalid={touched.cpf && !!errors.cpf}
                        />
                        {buscandoCNPJ && (
                          <InputGroup.Append>
                            <InputGroup.Text>
                              <Spinner animation="border" size="sm" />
                            </InputGroup.Text>
                          </InputGroup.Append>
                        )}
                      </InputGroup>
                      {touched.cpf && errors.cpf && (
                        <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>
                          {errors.cpf}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}
                {!values.internacional && (
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>IE</Form.Label>
                      <Form.Control
                        name="rg"
                        maxLength={16}
                        value={values.rg}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Form.Group>
                  </Col>
                )}
                <Col md={values.internacional ? 12 : 7}>
                  <Form.Group>
                    <Form.Label>
                      Razão Social <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="razao_social"
                      maxLength={40}
                      value={values.razao_social}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.razao_social && !!errors.razao_social}
                    />
                    <Form.Control.Feedback type="invalid">{errors.razao_social}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 3: CEP + Endereço + Bairro */}
              <Row>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>CEP</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="cep"
                        maxLength={9}
                        value={values.cep}
                        onChange={handleChange}
                        onBlur={async e => {
                          handleBlur(e);
                          await handleBuscarCEP(e.target.value, setFieldValue);
                        }}
                        placeholder="00000000"
                      />
                      {buscandoCEP && (
                        <InputGroup.Append>
                          <InputGroup.Text>
                            <Spinner animation="border" size="sm" />
                          </InputGroup.Text>
                        </InputGroup.Append>
                      )}
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={7}>
                  <Form.Group>
                    <Form.Label>Endereço</Form.Label>
                    <Form.Control
                      name="endereco"
                      maxLength={40}
                      value={values.endereco}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Bairro</Form.Label>
                    <Form.Control
                      name="bairro"
                      maxLength={20}
                      value={values.bairro}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 4: UF + Cidade */}
              <Row>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>UF</Form.Label>
                    <Form.Control
                      as="select"
                      name="estado"
                      value={values.estado}
                      onChange={e => {
                        var uf = e.target.value;
                        handleChange(e);
                        setFieldValue('codigo_cidade', null);
                        carregarCidades(uf);
                      }}
                    >
                      <option value="">UF</option>
                      {ESTADOS.map(function (item) {
                        return (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        );
                      })}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={10}>
                  <Form.Group>
                    <Form.Label>
                      Cidade <span className="text-danger">*</span>
                    </Form.Label>
                    <AsyncSelect
                      isDisabled={!values.estado || cidades.length === 0}
                      isLoading={loadingCidades}
                      loadingMessage={function () { return 'Carregando...'; }}
                      noOptionsMessage={function () {
                        return !values.estado ? 'Selecione o estado' : 'Digite uma cidade...';
                      }}
                      placeholder={!values.estado ? 'Selecione o estado' : 'Pesquisar cidades...'}
                      loadOptions={carregarCidadesAsync}
                      defaultOptions={cidades.slice(0, 20).map(function (c) {
                        return { value: c.codigo, label: c.descricao };
                      })}
                      onChange={function (option) {
                        setFieldValue('codigo_cidade', option ? option.value : null);
                        setFieldTouched('codigo_cidade', true);
                      }}
                      isClearable
                      value={getCidadeValue(values.codigo_cidade)}
                      components={{ DropdownIndicator: DropdownIndicator }}
                      styles={customStyles}
                    />
                    {touched.codigo_cidade && errors.codigo_cidade && (
                      <div className="text-danger" style={{ fontSize: '0.875em', marginTop: 4 }}>
                        {errors.codigo_cidade}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Linha 5: Telefones + Contato + Email */}
              <Row>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>
                      Fone 1 <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="fone_1"
                      maxLength={13}
                      value={values.fone_1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.fone_1 && !!errors.fone_1}
                    />
                    <Form.Control.Feedback type="invalid">{errors.fone_1}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Fone 2</Form.Label>
                    <Form.Control
                      name="fone_2"
                      maxLength={13}
                      value={values.fone_2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.fone_2 && !!errors.fone_2}
                    />
                    <Form.Control.Feedback type="invalid">{errors.fone_2}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Celular</Form.Label>
                    <Form.Control
                      name="fax"
                      maxLength={13}
                      value={values.fax}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.fax && !!errors.fax}
                    />
                    <Form.Control.Feedback type="invalid">{errors.fax}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Contato</Form.Label>
                    <Form.Control
                      name="contato"
                      maxLength={30}
                      value={values.contato}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>E-mail</Form.Label>
                    <Form.Control
                      name="email"
                      type="email"
                      maxLength={60}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && !!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ModalFornecedor;
